/* The Barback App: drink card (1 Nov 2026).
   Shared by the bar check result (nh-barcheck.js) and the menu page (nh-menu.js). Shows, for one drink:
   - a photo when one exists (PHOTOS below), otherwise the drink drawn as a glass to scale, stacked in pour order,
     the same way the Mixer User Guide draws it (mixer first, blue; spirit after, ink; toppers on top);
   - how long it takes, how many steps, and whether a shaker or blender is needed;
   - the steps, numbered, with amounts;
   - what is already in the mixer, so the venue sees there is nothing to cut, squeeze or muddle.
   Texts come from the "card" block in snippets/nh-barcheck-text.liquid (English and Norwegian).
   Usage: NHDrinkCard.html(drink, M, nameOf) where M is the bar check "menu" text block and nameOf(key) gives the name
   of an ingredient (house spirit or the venue's own brand). NHDrinkCard.easyLine(list, M) gives the menu headline. */
(function () {
  'use strict';
  /* Drink photos, by drink name. Empty until the photos are made; the drawn glass shows instead. */
  var PHOTOS = {};
  var TOPS = { soda: 1, tonic: 1, gingerBeer: 1, grapefruitSoda: 1, hProsecco: 1 };
  var SHAKEN = { Sour: 1, Daisy: 1, Fizz: 1 };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmt(s, o) { return String(s || '').replace(/\{(\w+)\}/g, function (m, k) { return k in o ? o[k] : m; }); }
  var DEC = '.';
  function n1(v) { return (v % 1 ? String(Math.round(v * 10) / 10) : String(v)).replace('.', DEC); }
  function cl(v) { return n1(v) + ' cl'; }
  function joinList(arr, and) {
    if (arr.length < 2) return arr.join('');
    return arr.slice(0, -1).join(', ') + ' ' + and + ' ' + arr[arr.length - 1];
  }
  function kind(key) {
    if (key === 'mix') return 'mix';
    if (TOPS[key]) return 'top';
    if (key === 'limeSJ') return 'sj';
    if (key === 'egg') return 'egg';
    return 'spirit';
  }

  /* The drink drawn in sharpie: the glass shape, the layers in pour order (toppers last), each layer hatched in its colour,
     ice where the drink is served on ice, and a handwritten label per layer. A wobble filter makes every line look hand drawn. */
  var SEQ = 0;
  var SHAPES = {
    /* outline, the inner area the liquid fills (for clipping), and where the liquid starts and stops (bottom, top) */
    highball: { o: 'M24 22 L30 200 Q62 207 94 200 L100 22', base: 'M31 194 Q62 199 93 194', clip: 'M26 22 L31 197 L93 197 L98 22 Z', lo: 194, hi: 44, ice: true },
    rocks: { o: 'M18 96 L26 200 Q62 207 98 200 L106 96', base: 'M27 192 Q62 197 97 192', clip: 'M20 96 L27 194 L97 194 L104 96 Z', lo: 191, hi: 112, ice: true },
    wine: { o: 'M30 26 C24 96 40 138 62 140 C84 138 100 96 94 26 M62 140 L62 198 M36 204 Q62 195 88 204', clip: 'M30 26 C24 96 40 138 62 140 C84 138 100 96 94 26 Z', lo: 138, hi: 52, ice: true },
    coupe: { o: 'M6 44 C10 108 36 130 62 130 C88 130 114 108 118 44 M62 130 L62 198 M36 204 Q62 195 88 204', clip: 'M6 44 C10 108 36 130 62 130 C88 130 114 108 118 44 Z', lo: 128, hi: 56, ice: false }
  };
  var INK = '#111111', BLUE = '#437BD9';
  function hatch(id, colour, gap, width, angle) {
    return '<pattern id="' + id + '" width="' + gap + '" height="' + gap + '" patternUnits="userSpaceOnUse" patternTransform="rotate(' + angle + ')">' +
      '<line x1="0" y1="0" x2="0" y2="' + gap + '" stroke="' + colour + '" stroke-width="' + width + '" stroke-linecap="round"/></pattern>';
  }
  function glassSvg(d, C, nameOf) {
    var id = 'nhdc' + (++SEQ);
    var g = SHAPES[d.g] || (d.s === 'Frozen' ? SHAPES.rocks : SHAPES.highball);
    var onIce = g.ice && d.s !== 'Frozen' && d.s !== 'Daisy';
    var parts = d.p.filter(function (p) { return !TOPS[p[0]]; }).concat(d.p.filter(function (p) { return TOPS[p[0]]; }));
    var total = parts.reduce(function (a, p) { return a + p[1]; }, 0);
    var span = g.lo - g.hi, y = g.lo, layers = '', lines = '', labels = [], fills = {
      mix: 'url(#' + id + 'm)', spirit: 'url(#' + id + 's)', sj: 'url(#' + id + 'j)', egg: 'url(#' + id + 'e)', top: 'url(#' + id + 't)'
    };
    parts.forEach(function (p, i) {
      var h = span * p[1] / total, k = kind(p[0]);
      y -= h;
      layers += '<rect x="0" y="' + y.toFixed(1) + '" width="124" height="' + (h + 0.6).toFixed(1) + '" fill="' + fills[k] + '"/>';
      if (i < parts.length - 1) lines += '<path d="M8 ' + y.toFixed(1) + ' q28 -2.5 56 0 t56 0" fill="none" stroke="' + INK + '" stroke-width="2"/>';
      labels.push({ y: y + h / 2, amt: cl(p[1]), name: nameOf(p[0], d), k: k });
    });
    /* top of the drink */
    lines += '<path d="M8 ' + y.toFixed(1) + ' q28 -2.5 56 0 t56 0" fill="none" stroke="' + INK + '" stroke-width="2.4"/>';
    var ice = '';
    if (onIce) {
      var cy = y + 6, sz = Math.min(26, Math.max(18, span / 5));
      [[34, cy + 4, -12], [60, cy + sz * 0.9, 9], [44, cy + sz * 1.9, -4]].forEach(function (c) {
        if (c[1] + sz < g.lo - 4) ice += '<rect x="' + c[0] + '" y="' + c[1].toFixed(1) + '" width="' + sz + '" height="' + sz + '" rx="3" fill="rgba(255,255,255,.35)" stroke="' + INK + '" stroke-width="2" transform="rotate(' + c[2] + ' ' + (c[0] + sz / 2) + ' ' + (c[1] + sz / 2).toFixed(1) + ')"/>';
      });
    }
    /* labels to the right, kept at least 24 px apart, each with a short sharpie leader line to its layer */
    labels.sort(function (a, b) { return b.y - a.y; });
    for (var i = 1; i < labels.length; i++) if (labels[i - 1].y - labels[i].y < 26) labels[i].ly = (labels[i - 1].ly || labels[i - 1].y) - 26;
    var top = labels.reduce(function (m, l) { return Math.min(m, (l.ly || l.y) - 16); }, Math.min(g.lo - span - 30, 210));
    top = Math.max(0, Math.floor(Math.min(top, /M\d+ (\d+)/.test(g.o) ? +RegExp.$1 - 6 : 0)));
    var lab = labels.map(function (l) {
      var ly = l.ly || l.y;
      return '<path d="M104 ' + l.y.toFixed(1) + ' Q116 ' + ((l.y + ly) / 2).toFixed(1) + ' 128 ' + ly.toFixed(1) + '" fill="none" stroke="' + INK + '" stroke-width="1.8"/>' +
        '<text x="134" y="' + (ly + 6).toFixed(1) + '" class="nh-dc__hand"><tspan class="nh-dc__hand-amt' + (l.k === 'mix' ? ' is-mix' : '') + '">' + esc(l.amt) + '</tspan> ' + esc(l.name) + '</text>';
    }).join('');
    var defs = '<defs>' +
      '<filter id="' + id + 'w" x="-5%" y="-5%" width="110%" height="110%"><feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="2" seed="' + (SEQ % 7 + 1) + '"/><feDisplacementMap in="SourceGraphic" scale="2.6"/></filter>' +
      hatch(id + 'm', BLUE, 5, 2.6, 35) + hatch(id + 's', INK, 6, 1.6, -35) + hatch(id + 'j', '#7FA33A', 5, 2.2, 60) +
      '<pattern id="' + id + 'e" width="12" height="9" patternUnits="userSpaceOnUse"><path d="M1 6 q2.5 -4 5 0 t5 0" fill="none" stroke="' + INK + '" stroke-width="1.3"/></pattern>' +
      '<pattern id="' + id + 't" width="22" height="20" patternUnits="userSpaceOnUse"><circle cx="5" cy="6" r="2.2" fill="none" stroke="' + BLUE + '" stroke-width="1.5"/><circle cx="15" cy="14" r="1.6" fill="none" stroke="' + BLUE + '" stroke-width="1.4"/><circle cx="18" cy="4" r="1.2" fill="none" stroke="' + BLUE + '" stroke-width="1.2"/></pattern>' +
      '<clipPath id="' + id + 'c"><path d="' + (g.clip) + '"/></clipPath></defs>';
    var label = fmt(C.inGlass, { cl: n1(total) });
    var aria = parts.map(function (p) { return cl(p[1]) + ' ' + nameOf(p[0], d); }).join(', ');
    return '<figure class="nh-dc__glass"><svg viewBox="0 ' + top + ' 330 ' + (212 - top) + '" width="330" height="' + (212 - top) + '" role="img" aria-label="' + esc(aria) + '">' + defs +
      '<g filter="url(#' + id + 'w)">' +
        '<g clip-path="url(#' + id + 'c)">' + layers + ice + lines + '</g>' +
        '<path d="' + g.o + '" fill="none" stroke="' + INK + '" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/>' +
        (g.base ? '<path d="' + g.base + '" fill="none" stroke="' + INK + '" stroke-width="2.4" stroke-linecap="round"/>' : '') +
        lab +
      '</g></svg>' +
      '<figcaption class="nh-dc__hand-cap">' + esc(label) + '</figcaption></figure>';
  }

  /* The steps, built from the recipe and its style (the methods in the Mixer User Guide). */
  /* The part of a drink that can be mixed before service: everything except bubbles and egg white (as in the batching guide). */
  function batchBase(d) { return d.p.filter(function (p) { return !TOPS[p[0]] && p[0] !== 'egg'; }); }
  function canBatch(d) { return batchBase(d).length >= 2; }

  /* Seconds per step behind a busy bar (estimates; Edwin to confirm). The card's time and the batched "% faster" come from these. */
  var SEC = { ice: 5, pour: 5, top: 4, stir: 5, shake: 12, strain: 6, fineStrain: 8, blend: 15, pourOut: 5, garnish: 3 };
  function secs(list) { return list.reduce(function (a, s) { return a + (s.sec || 0); }, 0); }
  function steps(d, C, nameOf, batched) {
    var S = C.s || {}, glass = (C.glassIn || {})[d.g] || '', out = [];
    var pours = d.p.filter(function (p) { return !TOPS[p[0]]; }), tops = d.p.filter(function (p) { return TOPS[p[0]]; });
    if (batched) {
      var base = batchBase(d), amt = base.reduce(function (a, p) { return a + p[1]; }, 0);
      pours = [['__batch', amt, base]].concat(pours.filter(function (p) { return p[0] === 'egg'; }));
    }
    function pour(p) {
      if (p[0] === '__batch') {
        return { k: 'batch', sec: SEC.pour, t: fmt(S.pourBatch, { amt: cl(p[1]) }), sub: p[2].map(function (b) { return nameOf(b[0], d); }).join(' + ') };
      }
      return { k: kind(p[0]), sec: SEC.pour, t: fmt(S.pour, { amt: cl(p[1]), name: nameOf(p[0], d) }) };
    }
    if (SHAKEN[d.s]) {
      pours.forEach(function (p) { out.push(pour(p)); });
      out.push({ k: 'act', sec: SEC.shake, t: S.shake });
      out.push(d.g === 'coupe' ? { k: 'act', sec: SEC.fineStrain, t: fmt(S.fineStrain, { glass: glass }) } : { k: 'act', sec: SEC.strain, t: fmt(S.strainIce, { glass: glass }) });
    } else if (d.s === 'Frozen') {
      pours.forEach(function (p) { out.push(pour(p)); });
      out.push({ k: 'act', sec: SEC.blend, t: S.blend });
      out.push({ k: 'act', sec: SEC.pourOut, t: fmt(S.pourOut, { glass: glass }) });
    } else {
      out.push({ k: 'ice', sec: SEC.ice, t: fmt(S.ice, { glass: glass }) });
      pours.forEach(function (p) { out.push(pour(p)); });
      if (d.s === 'Classic') out.push({ k: 'act', sec: SEC.stir, t: S.stir });
    }
    if (tops.length) {
      out.push({ k: 'top', sec: SEC.top, t: fmt(S.top, { what: joinList(tops.map(function (p) { return cl(p[1]) + ' ' + nameOf(p[0], d); }), C.and) }) });
    }
    if (d.gar) out.push({ k: 'gar', sec: SEC.garnish, t: fmt(S.garnish, { name: d.gar }) });
    return out;
  }

  var TOOL = {
    none: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5 9-10"/></svg>',
    time: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l3 2M10 2h4"/></svg>'
  };

  function html(d, M, nameOf) {
    var C = (M && M.card) || {}, lvl = d.l || 1;
    DEC = C.dec || '.';
    var list = steps(d, C, nameOf);
    var tool = d.s === 'Frozen' ? C.blender : (SHAKEN[d.s] ? C.shaker : C.noShaker);
    var photo = PHOTOS[d.n];
    var visual = photo
      ? '<figure class="nh-dc__photo"><img src="' + esc(photo) + '" alt="' + esc(d.n) + '" loading="lazy" width="600" height="750"></figure>'
      : glassSvg(d, C, nameOf);
    var inMix = ((C.inMixer || {})[d.m] || []);
    return '<div class="nh-dc nh-dc--l' + lvl + '">' +
      '<div class="nh-dc__facts">' +
          '<ul class="nh-dc__chips">' +
            '<li>' + TOOL.time + esc(fmt(C.secs, { n: Math.max(10, Math.round(secs(list) / 5) * 5) })) + '</li>' +
            '<li' + (tool === C.noShaker ? ' class="is-ok"' : '') + '>' + TOOL.none + esc(tool || '') + '</li>' +
          '</ul>' +
      '</div>' +
      '<div class="nh-dc__path">' + visual + howHtml(d, C, nameOf, list) + '</div>' +
      (d.v ? '<p class="nh-dc__proof">' + esc(fmt(C.servedAt, { venue: d.v })) + '</p>' : '') +
      (inMix.length ? '<p class="nh-dc__inmix"><strong>' + esc(C.inMixerLead) + '</strong> ' + esc(fmt(C.inMixerText, { list: joinList(inMix, C.and) })) + '</p>' : '') +
    '</div>';
  }

  function olHtml(list) {
    return '<ol class="nh-dc__steps">' + list.map(function (s) {
      return '<li class="nh-dc__step nh-dc__step--' + s.k + '"><span>' + esc(s.t) +
        (s.sub ? '<small>' + esc(s.sub) + '</small>' : '') + '</span></li>';
    }).join('') + '</ol>';
  }
  /* The pathway: the drink at the top, then two ways to serve it, from scratch and batched before service (egg white and
     bubbles are never batched: they go in at service), so the venue sees how much shorter service gets. */
  function howHtml(d, C, nameOf, list) {
    if (!canBatch(d)) {
      return '<div class="nh-dc__line" aria-hidden="true"></div>' +
        '<div class="nh-dc__col nh-dc__col--one"><p class="nh-dc__col-h">' + esc(C.howTo) + '</p>' + olHtml(list) +
        '<p class="nh-dc__nobatch">' + esc(C.noBatch) + '</p></div>';
    }
    var b = steps(d, C, nameOf, true), base = batchBase(d).reduce(function (a, p) { return a + p[1]; }, 0);
    /* Steps both ways share (ice first, bubbles or shake last) sit on the path once, before and after the split.
       "x faster" compares only the part that differs: pouring one batch bottle instead of two to four bottles (Edwin, 1 Nov 2026). */
    var pre = 0, post = 0;
    while (pre < list.length && pre < b.length && list[pre].t === b[pre].t) pre++;
    while (post < list.length - pre && post < b.length - pre && list[list.length - 1 - post].t === b[b.length - 1 - post].t) post++;
    var start = list.slice(0, pre), end = list.slice(list.length - post);
    var midA = list.slice(pre, list.length - post), midB = b.slice(pre, b.length - post);
    function shared(x) { return x.length ? '<div class="nh-dc__line" aria-hidden="true"></div><div class="nh-dc__shared">' + olHtml(x) + '</div>' : ''; }
    return shared(start) +
      '<div class="nh-dc__branch" aria-hidden="true"></div><div class="nh-dc__compare">' +
      '<div class="nh-dc__col"><p class="nh-dc__col-h">' + esc(C.madeToOrder) + '</p>' + olHtml(midA) + '</div>' +
      '<div class="nh-dc__col nh-dc__col--batch"><p class="nh-dc__col-h">' + esc(C.batched) + '</p>' + olHtml(midB) +
        '<p class="nh-dc__faster">' + esc(fmt(C.faster, { n: n1(Math.round(midA.length / midB.length * 10) / 10) })) + '</p></div>' +
      '</div>' +
      (end.length ? '<div class="nh-dc__merge" aria-hidden="true"></div><div class="nh-dc__shared">' + olHtml(end) + '</div>' : '');
  }

  /* One line for the top of the menu: how many of the drinks anyone on shift can make. */
  function easyLine(list, M) {
    var C = (M && M.card) || {}, n = list.filter(function (d) { return (d.l || 1) === 1; }).length;
    if (n === list.length) return fmt(C.easyAll, { n: n });
    if (n > 0) return fmt(C.easySome, { n: n, total: list.length });
    if (list.every(function (d) { return (d.l || 1) <= 2; })) return C.easyBartender || '';
    return '';
  }

  /* ---------- Selling the menu (stress test, 1 Nov 2026) ---------- */
  var API = '/apps/barback';
  var BAG_CL = 300; /* one 3 litre bag-in-box */

  /* Bags of each mixer for about a week of service: the weekly cocktails spread evenly over the 5 drinks, at least 1 bag
     (the smallest unit), at most 4. A low first order is easier to say yes to. */
  function bags(list, weekly) {
    var out = {}, per = (weekly || 75) / Math.max(1, list.length);
    list.forEach(function (d) {
      var mix = 0;
      d.p.forEach(function (p) { if (p[0] === 'mix') mix += p[1]; });
      out[d.m] = Math.max(1, Math.min(4, Math.ceil(per * mix / BAG_CL)));
    });
    return out;
  }

  /* Kroner left per week after ingredients, excl. MVA: menu price without MVA (25 %, alcohol-free 15 %) minus cost per serve,
     averaged over the drinks, times the cocktails a week. Rounded to the nearest 100 kr. */
  function profit(list, prices, weekly) {
    if (!weekly || !list.length) return 0;
    var sum = 0, n = 0;
    list.forEach(function (d) {
      var price = d.af ? prices.af : prices.drink;
      if (!price) return;
      sum += price / (d.af ? 1.15 : 1.25) - d.c; n += 1;
    });
    return n ? Math.round(sum / n * weekly / 100) * 100 : 0;
  }

  /* Everything the menu needs, by order form product name: the mixers (bags for about two weeks), Lime Super Juice when a
     recipe uses it (one bag), and the garnish named in the recipes (one pack each, once the recipe library has them). */
  function kit(list, weekly, names) {
    var want = bags(list, weekly), out = [];
    list.forEach(function (d) { if (want[d.m]) { out.push({ title: names[d.m], qty: want[d.m], kind: 'mix' }); delete want[d.m]; } });
    if (list.some(function (d) { return d.p.some(function (p) { return p[0] === 'limeSJ'; }); })) out.push({ title: 'Lime Super Juice', qty: 1, kind: 'sj' });
    var seen = {};
    list.forEach(function (d) { if (d.gar && !seen[d.gar]) { seen[d.gar] = 1; out.push({ title: d.gar, qty: 1, kind: 'gar' }); } });
    return out;
  }

  /* "Get everything for this menu": reads the order form as this venue sees it (so venues with their own prices get
     their own products), puts the kit in the cart and opens the order form with the cart open.
     Only for venues that can order; everyone else goes to the sign-up. */
  function orderMenu(list, weekly, orderUrl, names) {
    var want = kit(list, weekly, names);
    return fetch(orderUrl, { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html'), items = [];
      var cards = Array.prototype.slice.call(doc.querySelectorAll('.nh-card[data-variant][data-title]'));
      want.forEach(function (w) {
        var card = cards.filter(function (c) { return c.getAttribute('data-title') === w.title; })[0];
        if (card) items.push({ id: parseInt(card.getAttribute('data-variant'), 10), quantity: w.qty });
      });
      if (!items.length) throw new Error('no products');
      return fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }) });
    }).then(function (r) {
      if (!r.ok) throw new Error('cart ' + r.status);
      return orderUrl + (orderUrl.indexOf('?') > -1 ? '&' : '?') + 'cart=open';
    });
  }

  /* Counts how far visitors get (no personal data: only the step name and the day, counted on our server). */
  var sent = {};
  function track(name, once) {
    if (once && sent[name]) return;
    sent[name] = 1;
    try { fetch(API + '/event?e=' + encodeURIComponent(name), { method: 'POST', credentials: 'same-origin', keepalive: true }).catch(function () {}); } catch (e) {}
  }

  /* A link that opens this menu on the menu page, for the owner or the bar manager. The menu travels in the link itself
     (after #, so it never reaches a server log); house spirits only, no brands. */
  function b64(s) { return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); }
  function unb64(s) { s = s.replace(/-/g, '+').replace(/_/g, '/'); while (s.length % 4) s += '='; return decodeURIComponent(escape(atob(s))); }
  function shareLink(menu, menuUrl) {
    var m = { v: 1, venue: menu.venue || '', prices: menu.prices || {}, vol: menu.vol || 0,
      drinks: (menu.drinks || []).map(function (d) { return { n: d.n, m: d.m, s: d.s, g: d.g, l: d.l, af: d.af, c: d.c, p: d.p, gar: d.gar || '', v: d.v || '' }; }) };
    return menuUrl.replace(/#.*$/, '') + '#m=' + b64(JSON.stringify(m));
  }
  function fromLink(hash) {
    var m = /[#&]m=([A-Za-z0-9_-]+)/.exec(hash || '');
    if (!m) return null;
    try { var menu = JSON.parse(unb64(m[1])); return menu && Array.isArray(menu.drinks) && menu.drinks.length ? menu : null; } catch (e) { return null; }
  }
  function share(url, title, text, done) {
    if (navigator.share) { navigator.share({ title: title, text: text, url: url }).then(function () { done('shared'); }).catch(function () {}); return; }
    if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(url).then(function () { done('copied'); }, function () { location.href = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(text + '\n\n' + url); }); return; }
    location.href = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(text + '\n\n' + url);
  }

  /* One line saying what goes in the order, e.g. "5 cocktail mixers (1 bag each) and 1 Lime Super Juice". */
  function kitLine(list, weekly, names, C) {
    var k = kit(list, weekly, names), mix = k.filter(function (x) { return x.kind === 'mix'; }), rest = k.filter(function (x) { return x.kind !== 'mix'; });
    var bagsTotal = mix.reduce(function (a, x) { return a + x.qty; }, 0);
    var parts = [fmt(bagsTotal === mix.length ? C.kitMixEach : C.kitMix, { n: mix.length, bags: bagsTotal })];
    rest.forEach(function (x) { parts.push(x.qty + ' ' + x.title); });
    return joinList(parts, C.and);
  }

  window.NHDrinkCard = { html: html, easyLine: easyLine, photos: PHOTOS, bags: bags, kit: kit, kitLine: kitLine, profit: profit, orderMenu: orderMenu,
    track: track, shareLink: shareLink, fromLink: fromLink, share: share };
})();
